import React, { useState, useEffect } from 'react';
import { useOrderSelection } from '@/hooks/useOrderSelection';
import { useAutoHideMessage } from '@/hooks/useAutoHideMessage';
import { useIsMounted } from '@/hooks/useIsMounted';
import { formatDate, formatDateTime } from '@/utils/formatters';
import OrderSectionHeader from './OrderSectionHeader';
import OrderToolbar from './OrderToolbar';
import TableWrapper from './TableWrapper';
import styles from './OrderManagement.module.css';
import shared from '@/components/utils/shared.module.css';
import { Button, Note, Placeholder } from '@/components/ui';
import OrderStatusPill from './orderStatus';

/** Minimal shape every processed instruction must satisfy. */
export interface BaseProcessedOrder {
  id: number;
  order_date: string;
  created_at: string;
  scheme: { id: number; code: string } | { id: number; name: string; code?: string } | null;
  company: { id: number; symbol: string } | { id: number; symbol: string; company_name: string } | null;
  order_status_display?: string;
}

type Side = 'buy' | 'sell';

interface DeleteResult {
  error?: string;
  deleted_count?: number;
}

/**
 * Column config for the polymorphic "order type / cash environment" column.
 * Equity: { header: 'Order Type', getValue: o => o.order_type_display ?? '—' }
 * ETF:    { header: 'Cash Environment', getValue: o => o.cash_env_display ?? '—' }
 */
export interface OrderTypeColumnConfig<TOrder> {
  header: string;
  getValue: (order: TOrder) => string;
}

export interface BaseProcessedInstructionsProps<
  TBuy extends BaseProcessedOrder,
  TSell extends BaseProcessedOrder,
> {
  /** Prefix for section titles and messages, e.g. "ETF" or "" */
  titlePrefix?: string;
  fetchBuyOrders: () => Promise<{ orders: TBuy[] }>;
  fetchSellOrders: () => Promise<{ orders: TSell[] }>;
  deleteBuyOrders: (payload: { order_ids: number[] }) => Promise<DeleteResult>;
  deleteSellOrders: (payload: { order_ids: number[] }) => Promise<DeleteResult>;
  onDeleteSuccess: () => void;
  /** Config for the dynamic order-type / cash-environment column */
  buyOrderTypeColumn: OrderTypeColumnConfig<TBuy>;
  sellOrderTypeColumn: OrderTypeColumnConfig<TSell>;
  /** Render side-specific <th> headers (after the shared ones) */
  renderBuyHeaders: () => React.ReactNode;
  renderSellHeaders: () => React.ReactNode;
  /** Render side-specific <td> cells for a single row */
  renderBuyRow: (order: TBuy) => React.ReactNode;
  renderSellRow: (order: TSell) => React.ReactNode;
}

function BaseProcessedInstructions<
  TBuy extends BaseProcessedOrder,
  TSell extends BaseProcessedOrder,
>({
  titlePrefix = '',
  fetchBuyOrders,
  fetchSellOrders,
  deleteBuyOrders,
  deleteSellOrders,
  onDeleteSuccess,
  buyOrderTypeColumn,
  sellOrderTypeColumn,
  renderBuyHeaders,
  renderSellHeaders,
  renderBuyRow,
  renderSellRow,
}: BaseProcessedInstructionsProps<TBuy, TSell>): React.ReactElement {
  const [buyOrders, setBuyOrders] = useState<TBuy[]>([]);
  const [sellOrders, setSellOrders] = useState<TSell[]>([]);
  const { selectedBuy, selectedSell, toggleAll, toggle, clearSide } = useOrderSelection();
  const [loading, setLoading] = useState(true);
  const [processingAction, setProcessingAction] = useState<Side | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const isMounted = useIsMounted();

  useAutoHideMessage(successMessage, () => setSuccessMessage(null));

  const prefix = titlePrefix ? `${titlePrefix} ` : '';

  const loadInstructions = async () => {
    try {
      setLoading(true);
      setError(null);
      const [buyRes, sellRes] = await Promise.all([fetchBuyOrders(), fetchSellOrders()]);
      if (!isMounted.current) return;
      setBuyOrders(buyRes.orders);
      setSellOrders(sellRes.orders);
    } catch {
      if (isMounted.current) setError(`Failed to load ${prefix}processed instructions`);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  useEffect(() => { loadInstructions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (side: Side) => {
    const selected = side === 'buy' ? selectedBuy : selectedSell;
    if (selected.size === 0) { setError('Please select at least one instruction to delete'); return; }
    try {
      setProcessingAction(side);
      setError(null);
      setSuccessMessage(null);
      const fn = side === 'buy' ? deleteBuyOrders : deleteSellOrders;
      const result = await fn({ order_ids: Array.from(selected) });
      if (result.error) { setError(result.error); return; }
      setSuccessMessage(`Deleted ${result.deleted_count} ${prefix}${side} instruction(s)`);
      clearSide(side);
      await loadInstructions();
      onDeleteSuccess();
    } catch (err: unknown) {
      const msg = (err as { error?: string; message?: string })?.error
        ?? (err as { message?: string })?.message;
      setError(msg ?? `Failed to delete ${prefix}${side} instructions`);
    } finally {
      setProcessingAction(null);
    }
  };

  const renderSection = (side: Side) => {
    const orders = side === 'buy' ? buyOrders : sellOrders;
    const selected = side === 'buy' ? selectedBuy : selectedSell;
    const label = side === 'buy' ? 'Buy' : 'Sell';
    const isDeleting = processingAction === side;
    const orderTypeCol = side === 'buy'
      ? (buyOrderTypeColumn as OrderTypeColumnConfig<BaseProcessedOrder>)
      : (sellOrderTypeColumn as OrderTypeColumnConfig<BaseProcessedOrder>);

    return (
      <div className={styles['order-section']}>
        <OrderSectionHeader
          side={side}
          title={`Processed ${prefix}${label} Instructions`}
          right={<span className={styles['toolbar-info']}>{orders.length} instruction{orders.length !== 1 ? 's' : ''}</span>}
        />

        {orders.length === 0 ? (
          <div className={styles.empty}>
            <Placeholder message={<>No processed {prefix.toLowerCase()}{label.toLowerCase()} instructions found.</>} />
          </div>
        ) : (
          <>
            <OrderToolbar
              selectedCount={selected.size}
              totalCount={orders.length}
              actions={
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDelete(side)}
                  disabled={processingAction !== null || selected.size === 0}
                >
                  {isDeleting ? 'Deleting…' : `Delete Selected (${selected.size})`}
                </Button>
              }
            />

            <TableWrapper>
              <table className={`${shared.table} ${styles.table}`}>
                <thead>
                  <tr>
                    <th className={styles['col-check']}>
                      <input
                        type="checkbox"
                        checked={orders.length > 0 && selected.size === orders.length}
                        onChange={() => toggleAll(side, orders)}
                      />
                    </th>
                    <th>Order Date</th>
                    <th>{orderTypeCol.header}</th>
                    <th>Scheme</th>
                    <th>Company</th>
                    {side === 'buy' ? renderBuyHeaders() : renderSellHeaders()}
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => {
                    const isBuy = side === 'buy';
                    const schemeCode = (order.scheme as { code?: string })?.code ?? '—';
                    const companySymbol = order.company?.symbol ?? '—';
                    return (
                      <tr key={order.id}>
                        <td className={styles['col-check']}>
                          <input
                            type="checkbox"
                            checked={selected.has(order.id)}
                            onChange={() => toggle(order.id, side)}
                          />
                        </td>
                        <td>{formatDate(order.order_date)}</td>
                        <td>
                          <span className={styles['order-type-cell']}>
                            {orderTypeCol.getValue(order)}
                          </span>
                        </td>
                        <td>{schemeCode}</td>
                        <td>{companySymbol}</td>
                        {isBuy
                          ? renderBuyRow(order as TBuy)
                          : renderSellRow(order as TSell)}
                        <td><OrderStatusPill stage="pending" label={order.order_status_display ?? '—'} /></td>
                        <td>{formatDateTime(order.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableWrapper>
          </>
        )}
      </div>
    );
  };

  if (loading) return <Placeholder kind="loading" />;

  return (
    <div>
      {(successMessage || error) && (
        <div className={styles.messages}>
          {successMessage && <Note tone="success">{successMessage}</Note>}
          {error && <Note tone="error">{error}</Note>}
        </div>
      )}
      {renderSection('buy')}
      {renderSection('sell')}
    </div>
  );
}

export default BaseProcessedInstructions;
